import { formatMoney, formatNumber, useLocale, useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Panel,
  PanelAction,
  PanelBody,
  PanelContent,
  PanelHeader,
  PanelIcon,
  PanelMeta,
  PanelTitle,
} from '@multaqa/ui/components/ui/panel'
import { Skeleton } from '@multaqa/ui/components/ui/skeleton'
import { toast } from '@multaqa/ui/components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { PackageIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { ConfirmPostDialog } from '@/components/confirm-post-dialog'
import { InlineQueryError } from '@/components/inline-query-error'
import { formatErrorMessage } from '@/lib/error-message'
import { localizedText, meQuery, useEffectiveGrants } from '@/lib/me'
import {
  type SupplierProductLink,
  useRemoveSupplierProduct,
  useSupplierProducts,
} from '../supplier-products-api'
import { SupplierProductDialog } from './supplier-product-dialog'

/**
 * The products this supplier sells (WP06). Adding, editing and removing a
 * link are offered only to someone who may edit suppliers.
 */
export function SupplierProductsPanel({
  supplierId,
  currency,
}: {
  supplierId: string
  currency: string
}) {
  const { t } = useTranslation('products')
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const { can } = useEffectiveGrants()
  const companyId = me?.active_company_id ?? null
  const links = useSupplierProducts(companyId, supplierId)
  const remove = useRemoveSupplierProduct(companyId ?? '', supplierId)
  const [editing, setEditing] = useState<SupplierProductLink | 'new' | null>(null)
  const [removing, setRemoving] = useState<SupplierProductLink | null>(null)
  const canEdit = can('supplier.edit')

  if (!companyId) return null
  const rows = links.data ?? []

  return (
    <Panel data-testid="supplier-products">
      <PanelHeader>
        <PanelIcon>
          <PackageIcon />
        </PanelIcon>
        <div className="min-w-0">
          <PanelTitle>{t('supplierLinks.title')}</PanelTitle>
          <PanelMeta className="mt-1">{t('supplierLinks.description')}</PanelMeta>
        </div>
        {canEdit ? (
          <PanelAction>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing('new')}
              data-testid="supplier-products-add"
            >
              <PlusIcon className="size-3.5" aria-hidden="true" />
              {t('supplierLinks.add')}
            </Button>
          </PanelAction>
        ) : null}
      </PanelHeader>
      <PanelContent>
        {links.isLoading ? (
          <PanelBody>
            <Skeleton className="h-24 w-full" />
          </PanelBody>
        ) : links.isError ? (
          <PanelBody>
            <InlineQueryError
              title={t('supplierLinks.loadFailed')}
              description={t('suppliersTab.loadFailedDescription')}
              retryLabel={t('photos.retry')}
              onRetry={() => void links.refetch()}
            />
          </PanelBody>
        ) : rows.length === 0 ? (
          <PanelBody>
            <EmptyState
              title={t('supplierLinks.emptyTitle')}
              description={t('supplierLinks.emptyDescription')}
            />
          </PanelBody>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('supplierLinks.product')}</TableHead>
                <TableHead>{t('supplierLinks.supplierSku')}</TableHead>
                <TableHead className="text-end">{t('supplierLinks.price')}</TableHead>
                <TableHead className="text-end">{t('supplierLinks.leadTime')}</TableHead>
                {canEdit ? <TableHead className="w-24" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((link) => (
                <TableRow key={link.id} data-testid={`supplier-product-${link.id}`}>
                  <TableCell>
                    {localizedText(link.variant?.product_name, locale)}
                    <span className="text-muted-foreground block tabular-nums text-small">
                      <bdi dir="ltr">{link.variant?.sku}</bdi>
                    </span>
                    {link.is_preferred ? (
                      <Badge tone="success">{t('suppliersTab.preferred')}</Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {link.supplier_sku ? <bdi dir="ltr">{link.supplier_sku}</bdi> : '—'}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {link.unit_price && link.currency
                      ? formatMoney(link.unit_price, link.currency, locale)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {link.lead_time_days === null
                      ? '—'
                      : t('suppliersTab.days', {
                          count: link.lead_time_days,
                          days: formatNumber(link.lead_time_days, locale),
                        })}
                  </TableCell>
                  {canEdit ? (
                    <TableCell className="text-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditing(link)}
                        aria-label={t('supplierLinks.editNamed', { sku: link.variant?.sku ?? '' })}
                        data-testid={`supplier-product-edit-${link.id}`}
                      >
                        <PencilIcon className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setRemoving(link)}
                        aria-label={t('supplierLinks.removeNamed', {
                          sku: link.variant?.sku ?? '',
                        })}
                        data-testid={`supplier-product-remove-${link.id}`}
                      >
                        <Trash2Icon className="text-destructive size-4" aria-hidden="true" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PanelContent>

      {editing ? (
        <SupplierProductDialog
          companyId={companyId}
          supplierId={supplierId}
          currency={currency}
          link={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <ConfirmPostDialog
        open={removing !== null}
        title={t('supplierLinks.removeTitle')}
        description={t('supplierLinks.removeDescription', { sku: removing?.variant?.sku ?? '' })}
        confirmLabel={t('supplierLinks.remove')}
        pending={remove.isPending}
        testId="supplier-product-remove-dialog"
        onCancel={() => setRemoving(null)}
        onConfirm={() =>
          removing
            ? remove.mutate(removing.variant_id, {
                onSuccess: () => setRemoving(null),
                onError: (error) =>
                  toast.error(formatErrorMessage(error, t('supplierLinks.removeFailed'))),
              })
            : undefined
        }
      />
    </Panel>
  )
}
